import { startCase } from 'lodash-es'
import { TFunction } from 'react-i18next'
import * as yup from 'yup'
import { CUSTOMER_WALLET_CURRENCY_PAYMENT_METHODS_LABELS } from 'pages/Customers/constants'
import { PaymentMethods } from 'types/beneficiaries'
import {
  AddCustomerPayload,
  AddCustomerPayloadKeys,
  AddCustomerRequest,
  Address,
  businessCustomerFields,
  BusinessCustomerType,
  CustomerEntityType,
  individualCustomerFields,
  IndividualCustomerType,
} from 'types/customers'
import { NESTED_CUSTOMERS_ERROR_MAPPING } from './constants'

/**
 * Checks if the customer is a business customer.
 * @param {BusinessCustomerType | IndividualCustomerType} currentCustomer - The customer to check.
 * @returns {currentCustomer is BusinessCustomerType} - True if the customer is a business customer.
 */
export const isBusinessCustomer = (
  currentCustomer: BusinessCustomerType | IndividualCustomerType
): currentCustomer is BusinessCustomerType =>
  !!currentCustomer && currentCustomer?.type === CustomerEntityType.COMPANY

/**
 * Checks if the customer is an individual customer.
 * @param {BusinessCustomerType | IndividualCustomerType} currentCustomer - The customer to check.
 * @returns {currentCustomer is IndividualCustomerType} - True if the customer is an individual customer.
 */
export const isIndividualCustomer = (
  currentCustomer: BusinessCustomerType | IndividualCustomerType
): currentCustomer is IndividualCustomerType =>
  !!currentCustomer && currentCustomer?.type === CustomerEntityType.INDIVIDUAL

export const getCustomerFields = (customerFormState: AddCustomerPayload): AddCustomerRequest => {
  if (customerFormState?.customerEntityType === CustomerEntityType.COMPANY) {
    return {
      type: customerFormState?.customerEntityType,
      company: {
        name: customerFormState?.businessName || '',
        description: customerFormState?.businessDescription || '',
        taxResidenceCountryCode: customerFormState?.businessTaxResidency || '',
        registrationNumber: customerFormState?.registrationNumber || '',
        industryReference: customerFormState?.industry || '',
        monthlyExpectedVolumesReference: customerFormState?.businessMonthlyVolume || '',
        address: {
          addressLine1: customerFormState?.addressLine1 || '',
          addressLine2: customerFormState?.addressLine2 || null,
          city: customerFormState?.city || '',
          postalCode: customerFormState?.postalCode || '',
          countryCode: customerFormState?.countryCode || '',
        },
        representative: {
          firstName: customerFormState?.firstName || '',
          lastName: customerFormState?.lastName || '',
          dateOfBirth: customerFormState?.dateOfBirth || '',
          address: { countryCode: customerFormState?.countryCode || '' },
          ...(customerFormState?.nationality && {
            birthCountryCode: customerFormState?.nationality,
            nationality: customerFormState?.nationality,
          }),
        },
      },
    }
  } else if (customerFormState?.customerEntityType === CustomerEntityType.INDIVIDUAL) {
    const [day, month, year] = customerFormState.dateOfBirth.split(',')

    return {
      type: customerFormState?.customerEntityType,
      individual: {
        firstName: customerFormState?.firstName || '',
        lastName: customerFormState?.lastName || '',
        dateOfBirth: `${year}-${month}-${day}`,
        documentNumber: customerFormState?.personalIdNumber || '',
        birthCountryCode: customerFormState?.nationality || '',
        nationality: customerFormState?.nationality || '',
        residenceCountryCode: customerFormState.countryCode || '',
        address: {
          addressLine1: customerFormState?.addressLine1 || '',
          addressLine2: customerFormState?.addressLine2 || null,
          city: customerFormState.city || '',
          postalCode: customerFormState.postalCode || '',
          countryCode: customerFormState.countryCode || '',
          ...(customerFormState?.countryCode === 'US' && {
            stateCode: customerFormState.stateCode || '',
          }),
        },
        description: customerFormState?.individualCustomerDescription,
      },
    }
  }
}

interface ValidateInputFieldParams {
  t: TFunction<'translation', undefined>
  label: string
  minLength?: number
  maxLength?: number
  allowOnlyAlphanumericCharacters?: boolean
  allowOnlyAlphanumericAndSpecialCharacters?: boolean
  disallowNumericCharacters?: boolean
}

/**
 * Validates an input field with optional length and special character constraints.
 *
 * @param {ValidateInputFieldParams} params - The parameters for field validation.
 * @param {TFunction<'translation', undefined>} params.t - Translation function for localized error messages.
 * @param {string} params.label - The label or name of the field for display in error messages.
 * @param {number} [params.minLength] - Minimum length of the input value (optional).
 * @param {boolean} [params.allowOnlyAlphanumericCharacters] - Whether to restrict the input to only alphanumeric characters (optional).
 * @param {boolean} [params.allowOnlyAlphanumericAndSpecialCharacters] - Whether to allow alphanumeric and special characters (optional).
 * @param {boolean} [params.disallowNumericCharacters] - Whether to exclude digits from the input (optional).
 *
 * @returns {yup.StringSchema} - A Yup string schema for validating the input field.
 */
export const validateInputField = ({
  t,
  label,
  minLength,
  maxLength,
  allowOnlyAlphanumericCharacters,
  allowOnlyAlphanumericAndSpecialCharacters,
  disallowNumericCharacters,
}: ValidateInputFieldParams): yup.StringSchema => {
  const alphanumericWithSpacesRegex = /^[a-zA-Z0-9 ]*$/ // Matches alphanumeric and spaces
  const extendedAlphanumericRegex =
    /^[0-9a-zA-ZĄĮĀĆÄÅĘĒČÉŹĖĢĶĪĻŠŃŅÓŌÕÖŲŁŚŪÜŻŽąįāćäåęēčéźėģķīļšńņóōõöųłśūüżž –\-]+$/ // Allows accented characters, spaces, and dashes
  const nonNumericRegex = /^[^0-9]*$/ // Matches strings with no numbers

  const schema = yup
    .string()
    .trim()
    .required(t('fieldRequired', { field: label, interpolation: { escapeValue: false } }))
    .label(label)
    .test({
      test(value, ctx) {
        // Fields are required based on whether a business customer or an individual customer is being created
        const requiredFields: AddCustomerPayloadKeys[] =
          ctx?.parent?.customerEntityType === CustomerEntityType.COMPANY
            ? businessCustomerFields
            : individualCustomerFields

        const isRequired = requiredFields?.includes(ctx?.path as AddCustomerPayloadKeys)

        if (value) {
          if (!!minLength && value?.toString()?.length < minLength) {
            return ctx.createError({
              message: t('fieldMinLength', {
                field: label,
                min: minLength,
                interpolation: { escapeValue: false },
              }),
            })
          }

          if (!!maxLength && value?.toString()?.length > maxLength) {
            return ctx.createError({
              message: t('fieldMaxLength', {
                field: label,
                max: maxLength,
                interpolation: { escapeValue: false },
              }),
            })
          }

          if (
            !!allowOnlyAlphanumericCharacters &&
            allowOnlyAlphanumericCharacters &&
            !alphanumericWithSpacesRegex.test(value)
          ) {
            return ctx.createError({
              message: t('fieldAlphanumeric', {
                field: label,
                interpolation: { escapeValue: false },
              }),
            })
          }

          if (
            !!allowOnlyAlphanumericAndSpecialCharacters &&
            allowOnlyAlphanumericAndSpecialCharacters &&
            !extendedAlphanumericRegex.test(value)
          ) {
            return ctx.createError({
              message: t('registrationNumberInvalidCharacters', {
                field: label,
                interpolation: { escapeValue: false },
              }),
            })
          }

          if (
            !!disallowNumericCharacters &&
            disallowNumericCharacters &&
            !nonNumericRegex.test(value)
          ) {
            return ctx.createError({
              message: t('fieldNoNumericCharacters', {
                field: label,
                interpolation: { escapeValue: false },
              }),
            })
          }

          return true
        } else if (isRequired) {
          return ctx.createError({
            message: t('fieldRequired', {
              field: t('formFields.dateOfBirth'),
              interpolation: { escapeValue: false },
            }),
            type: 'required',
          })
        }

        return true
      },
    })

  return schema
}

/**
 * Gets wallet profile options for the customer wallet creation
 *
 * @param {string[]} paymentMethods - The payment methods available for the selected currency
 *
 * @returns {yup.StringSchema} - A Yup string schema for validating the input field.
 */
export const getOptions = (paymentMethods: string[]) => {
  const hasSepaAndSepaInstant =
    paymentMethods?.includes(PaymentMethods.SEPA_INSTANT?.toLowerCase()) ||
    paymentMethods?.includes(PaymentMethods.SEPA?.toLowerCase())

  const options: string[] = paymentMethods
    ?.filter(
      option =>
        option !== PaymentMethods.SEPA_INSTANT?.toLowerCase() &&
        option !== PaymentMethods.SEPA?.toLowerCase()
    )
    ?.map(
      paymentMethod =>
        CUSTOMER_WALLET_CURRENCY_PAYMENT_METHODS_LABELS?.[paymentMethod?.toUpperCase()] ??
        paymentMethod
    )

  if (hasSepaAndSepaInstant) {
    options?.push(CUSTOMER_WALLET_CURRENCY_PAYMENT_METHODS_LABELS.SEPA_AND_SEPA_INSTANT)
  }

  return options
}

export const getNestedCustomerAddress = (customerAddres: Address, countryName?: string) => {
  const { addressLine1, addressLine2, city, stateCode, postalCode, countryCode } =
    customerAddres || {}

  return [
    ...(addressLine1 ? [startCase(addressLine1?.toLowerCase())] : []),
    ...(addressLine2 ? [startCase(addressLine2?.toLowerCase())] : []),
    ...(city ? [startCase(city?.toLowerCase())] : []),
    ...(stateCode ? [stateCode] : []),
    ...(postalCode ? [postalCode] : []),
    ...(countryCode || countryName
      ? [countryName ? startCase(countryName?.toLowerCase()) : countryCode]
      : []),
  ]?.join(', ')
}

export const getNestedCustomersErrorFormKey = (errorKey: string) => {
  const mappedKey = NESTED_CUSTOMERS_ERROR_MAPPING[errorKey]
  if (!mappedKey) {
    return errorKey as keyof AddCustomerPayload
  }
  return mappedKey
}
